import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { publicApi } from "../services/api";
import { normalizeShortCode } from "../utils/shortCode";

const PAGE_BG = "#F9F5F0";
const ACCENT = "#DE810E";
const TEXT = "#1a1a1a";

// Same store links the marketing pages use (see components/dashboard.js and
// components/appRedirect.js). Recipients land here from a gift link, so the
// download prompt is the whole point of the page.
const APP_STORE_URL = "https://apps.apple.com/us/app/my-hoom/id6751858013";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.myhoom.myhoom";

function QrCodeClaimPage() {
  const [searchParams] = useSearchParams();
  const [purchaseData, setPurchaseData] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");
  const shortCode = useMemo(
    () => normalizeShortCode(searchParams.get("shortCode")),
    [searchParams],
  );

  const qrPayload = useMemo(() => {
    if (!shortCode) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams({ shortCode });
    return `${origin}/admin-dashboard/redeem-beer?${params.toString()}`;
  }, [shortCode]);

  const displayCode = shortCode;
  const senderName = purchaseData?.sender?.name;

  const corner = (position) => ({
    position: "absolute",
    width: 36,
    height: 36,
    borderColor: ACCENT,
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    ...position,
  });

  useEffect(() => {
    let cancelled = false;

    const loadPurchase = async () => {
      if (!shortCode) {
        setPurchaseData(null);
        setPurchaseError("");
        setPurchaseLoading(false);
        return;
      }

      setPurchaseLoading(true);
      setPurchaseError("");

      try {
        const data = await publicApi.getPurchaseByShortCode(shortCode);
        if (cancelled) return;
        setPurchaseData(data);
      } catch (error) {
        if (cancelled) return;
        setPurchaseData(null);
        setPurchaseError(
          error?.error || error?.message || "Failed to load purchase details.",
        );
      } finally {
        if (!cancelled) setPurchaseLoading(false);
      }
    };

    loadPurchase();

    return () => {
      cancelled = true;
    };
  }, [shortCode]);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        fontFamily: '"Poppins", sans-serif',
        color: TEXT,
        padding: "48px 20px 40px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h1
        style={{
          margin: "0 0 32px",
          fontSize: "1.5rem",
          fontWeight: 700,
          textAlign: "center",
          letterSpacing: "-0.02em",
        }}
      >
        Claim your beer!
      </h1>

      {shortCode ? (
        <>
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 16,
              padding: "28px 24px 32px",
              boxShadow: "0 8px 28px rgba(0, 0, 0, 0.08)",
              maxWidth: "100%",
              width: "min(100%, 320px)",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 12,
              }}
            >
              <span
                style={corner({
                  top: 0,
                  left: 23,
                  borderTopWidth: 3,
                  borderLeftWidth: 3,
                  borderTopLeftRadius: 18,
                })}
              />
              <span
                style={corner({
                  top: 0,
                  right: 23,
                  borderTopWidth: 3,
                  borderRightWidth: 3,
                  borderTopRightRadius: 18,
                })}
              />
              <span
                style={corner({
                  bottom: 5,
                  left: 23,
                  borderBottomWidth: 3,
                  borderLeftWidth: 3,
                  borderBottomLeftRadius: 18,
                })}
              />
              <span
                style={corner({
                  bottom: 5,
                  right: 23,
                  borderBottomWidth: 3,
                  borderRightWidth: 3,
                  borderBottomRightRadius: 18,
                })}
              />
              <div style={{ backgroundColor: "#fff" }}>
                <QRCode
                  value={qrPayload}
                  size={200}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </div>
            </div>
          </div>

          <p
            style={{
              margin: "28px 0 0",
              fontSize: "1rem",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Short code: {displayCode}
          </p>
          {purchaseError && (
            <p
              style={{
                margin: "10px 0 0",
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#B00020",
                textAlign: "center",
                maxWidth: 320,
                lineHeight: 1.4,
              }}
            >
              {purchaseError}
            </p>
          )}
          <p
            style={{
              margin: "12px 0 0",
              fontSize: "1rem",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Sender:{" "}
            {purchaseLoading ? "Loading..." : senderName || "Unknown sender"}
          </p>
          <p
            style={{
              margin: "20px 0 0",
              fontSize: "0.95rem",
              fontWeight: 700,
              textAlign: "center",
              lineHeight: 1.45,
              maxWidth: 320,
            }}
          >
            Download the my HOOM mobile app to redeem your gift
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              margin: "16px 0 0",
            }}
          >
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
                style={{ height: 40 }}
              />
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store"
                style={{ height: 40 }}
              />
            </a>
          </div>
        </>
      ) : (
        <p
          style={{
            marginTop: 8,
            fontSize: "0.95rem",
            fontWeight: 600,
            textAlign: "center",
            color: "#444",
            maxWidth: 300,
            lineHeight: 1.5,
          }}
        >
          Add a short code to open your claim. Example:{" "}
          <span style={{ wordBreak: "break-all" }}>
            /qrcode?shortCode=YON10KCV
          </span>
        </p>
      )}
    </div>
  );
}

export default QrCodeClaimPage;
